/**
 * Every state of the partner iframe, and the exact words the officer reads. Data rather than
 * markup so the copy can be reviewed on its own -- and so the state machine's tests can assert
 * against the same strings the components render, instead of a second copy of them.
 *
 * The copy is the design handoff's (design_handoff_epc_iframe/index.html), screen for screen:
 *   connecting / loading -> 1 Handshake          newOrder -> 2 Order + 3 Options & documents
 *   orderPlaced -> 4 Confirmation                processing -> 5 Processing (stepper)
 *   completed -> 6 Completed                     existingOrder -> 7 Existing order
 *   failed -> 8 Failed (only after processing)   needsCredentials -> 9 Update credentials
 *   notOnboarded -> 11a No entitlement           sessionExpired -> 11b Session expired
 *   error -> 11c Load error                      closed / cancelled -> 14 terminal states
 *   (12 Submit for redraw is a dialog that re-enters processing, not a state of its own.)
 *
 * What is REAL and what is not, today: the BFF has POST /epc/ui/origin and GET /epc/ui/orders/{id}.
 * Place order is live -- but through the Encompass host, which creates the transaction; our order
 * number arrives afterwards over ICE's webhook, which is why screen 5 comes BEFORE screen 4 in
 * production and the confirmation waits for the first status that carries a reference. There is
 * still no redraw endpoint (orders.js: canRedraw false); cancel is live as of F5 (canCancel true).
 * Under ?stubHost=1 the stub orders module simulates the mock's lifecycle so every screen is
 * clickable for review (stub-orders.js), and there the reference is known at once, so screen 4
 * comes first.
 */

export const PRODUCT = {
  firm: 'Polunsky Beitel Green',
  product: 'Closing Package Preparation',
  full: 'PBG Closing Package Preparation',
  tagline: 'The Home for Home Lenders.',
};

export const STATES = {
  connecting: {
    spinner: true,
    footer: false,
    title: 'Connecting to Encompass…',
    detail: 'Registering with the host. This only takes a moment.',
    pill: { label: 'Connecting', tone: 'processing' },
    controls: [],
  },
  loading: {
    spinner: true,
    footer: false,
    title: 'Loading this loan…',
    detail: 'Reading your loan context from Encompass. This only takes a moment.',
    pill: { label: 'Loading', tone: 'processing' },
    controls: [],
  },
  notOnboarded: {
    spinner: false,
    footer: false,
    icon: { glyph: '∅', tone: 'neutral' },
    title: "This product isn't enabled for your account",
    detail: `${PRODUCT.full} isn't currently part of your Encompass services. `
      + 'Your administrator can enable it, or contact PBG to get set up.',
    pill: { label: 'Not enabled', tone: 'neutral' },
    controls: ['close'],
  },
  needsCredentials: {
    spinner: false,
    footer: false,
    icon: { glyph: '⚿', tone: 'warn' },
    title: 'Your PBG service credentials need updating',
    // cert 1.14: generic, and never a field. The host owns the credential flow end to end.
    detail: 'To prepare documents for this loan, your PBG service connection needs to be refreshed. '
      + "This is handled securely by Encompass — you won't enter any credentials here.",
    pill: { label: 'Credentials needed', tone: 'warn' },
    controls: ['updateCredentials', 'close'],
  },
  error: {
    spinner: false,
    footer: false,
    icon: { glyph: '!', tone: 'danger' },
    title: "We couldn't load this product",
    detail: `Something went wrong starting ${PRODUCT.full}. Please try again in a moment.`,
    pill: { label: 'Error', tone: 'failed' },
    controls: ['retry', 'close'],
  },
  sessionExpired: {
    spinner: true,
    footer: false,
    title: 'Your session expired — reconnecting…',
    detail: "For security, PBG sessions are short-lived. We're re-establishing your secure connection. "
      + "If this doesn't resolve, you can reconnect manually.",
    pill: { label: 'Reconnecting', tone: 'warn' },
    controls: ['reconnect'],
  },
  newOrder: {
    spinner: false,
    footer: true,
    title: 'Order closing documents',
    detail: 'Review the loan context below, then start your order. '
      + "PBG prepares your closing package and delivers it to this loan's eFolder.",
    callout: 'Loan data is provided by Encompass and shown read-only. '
      + 'PBG receives only the contracted fields needed to prepare your documents.',
    pill: { label: 'New order', tone: 'processing' },
    controls: ['startOrder'],
    // Screen 3 of the mock: the second view of the same machine state.
    options: {
      title: 'Order options & documents',
      detail: (borrowers) => `Choose what PBG should prepare for ${firstNames(borrowers)}. `
        + 'You can keep working in Encompass while we prepare them.',
      // Only rendered if canCreate is turned back off (orders.js): a greyed-out button with no
      // explanation is worse than the feature being off.
      placeOrderNote: 'Placing orders is unavailable just now. Your selections here are not sent anywhere yet.',
      // The other reason the button is dead: nothing is ticked. The picker speaks its own rule
      // only once the officer has touched the list, so until then this is the ONLY explanation on
      // the screen -- rendered whenever Place order is disabled for want of a document, and
      // pointed at by the button's aria-describedby.
      placeOrderDocumentNote: 'Select at least one document to prepare.',
      // A create that timed out leaves the officer on THIS screen with Place order live again, and
      // TOASTS.placeTimedOut -- the only record that a duplicate transaction may exist -- gone in
      // seconds. So the warning is repeated here as a standing callout that outlives the toast: it
      // appears when a create times out and stays until an order actually goes through, or the
      // iframe is reloaded. Pointed at by Place order's aria-describedby while it is up, because
      // the button it qualifies is the dangerous one.
      timedOutCalloutLead: "We didn't get a confirmation from Encompass.",
      timedOutCallout: "This loan's services list may already show an order. Check it before "
        + 'ordering again — ordering again could place a second one.',
      placing: 'Placing order…',
      controls: ['back', 'placeOrder'],
    },
  },
  orderPlaced: {
    spinner: false,
    footer: true,
    title: 'Order placed',
    detail: "We've received your request and started preparing your documents.",
    callout: "We'll prepare your documents and deliver them to this loan's eFolder automatically.",
    calloutLead: 'Submitted to PBG.',
    pill: { label: 'Order placed', tone: 'processing' },
    controls: ['viewStatus'],
  },
  processing: {
    spinner: false,
    footer: true,
    title: 'Preparing your documents',
    detail: 'This usually takes a few minutes.',
    calloutLead: 'You can keep working.',
    callout: "Documents are prepared on PBG's servers — leave this open or come back later. "
      + "This screen updates automatically when they're ready, and the documents land in your eFolder.",
    // Step 0's sub-copy while the order exists in Encompass but has no PBG order number yet:
    // the officer placed it, ICE's webhook is on its way, and there is nothing to quote yet.
    awaitingReference: 'Order received — assigning an order number…',
    pill: { label: 'Processing', tone: 'processing' },
    controls: [],
  },
  completed: {
    spinner: false,
    footer: true,
    title: 'Documents delivered',
    detail: "Your closing package is ready and filed in this loan's eFolder.",
    redrawNote: 'Need a change after delivery? You can submit a redraw on this completed order.',
    pill: { label: 'Completed', tone: 'completed' },
    controls: ['done', 'redraw'],
  },
  completedDeliveryPending: {
    spinner: true,
    footer: true,
    title: 'Completed — delivering your documents…',
    detail: "Your closing package is ready and is on its way to this loan's eFolder. "
      + 'This screen updates when it lands — you can keep working in Encompass.',
    pill: { label: 'Delivering', tone: 'processing' },
    controls: ['close'],
  },
  completedDeliveryFailed: {
    spinner: false,
    footer: true,
    icon: { glyph: '!', tone: 'failed' },
    title: "Completed — we couldn't deliver to your eFolder",
    // Two things the officer needs and cannot infer: the work is DONE (so they must not re-order
    // it) and somebody already knows (so they need not report it). Both stated plainly, not left
    // to be inferred from "prepared" or "notified" alone.
    detail: 'Your closing package has been fully prepared — that part is done, and there is '
      + "nothing to re-order. It just hasn't reached this loan's eFolder yet. We've already been "
      + "notified and are working on delivering it, so there's nothing you need to report either. "
      + 'Quote the reference below if you do contact PBG.',
    pill: { label: 'Delivery failed', tone: 'failed' },
    controls: ['close'],
  },
  existingOrder: {
    spinner: false,
    footer: true,
    title: 'Your current order',
    detail: 'This loan already has a PBG closing-package order in progress. '
      + "We've opened it for you instead of starting a new one.",
    // "...in progress" above a Completed/Canceled/Failed pill is the same contradiction the
    // processingNote callout used to produce -- #existingOrder picks this instead once its own
    // `terminal` is true.
    terminalDetail: "This loan has a PBG closing-package order. We've opened it for you.",
    // The screen watches a running order itself since the 2026-10-14 UAT retest (finding 7): it
    // used to say "updates when you refresh" and sat on Processing after the order completed.
    processingNote: 'Documents are being prepared. This screen updates as PBG works — you can keep working in Encompass.',
    pill: null, // mirrors the order's own status; see pillForOrder
    controls: [],
  },
  failed: {
    spinner: false,
    footer: true,
    icon: { glyph: '!', tone: 'danger' },
    title: 'We hit a problem preparing your documents',
    // There is no re-run in ICE's model: the transaction that failed is final, and Try again
    // creates a SECOND one. The copy has to say so, or the officer reads "try again" as a retry of
    // the same order and loses track of the number support will ask them for.
    detail: "Your order didn't complete. Nothing was filed to the eFolder. This order stays on file "
      + 'under its own order number and cannot be re-run, so Try again places a new order. '
      + "You can also contact PBG support with that number and we'll take it from here.",
    pill: { label: 'Failed', tone: 'failed' },
    controls: ['retryOrder', 'support'],
  },
  closed: {
    spinner: false,
    footer: false,
    terminal: true,
    icon: { glyph: '✓', tone: 'success' },
    title: 'Returned to Encompass',
    detail: 'Control was handed back to Encompass. If this window is still open, you can close it.',
    pill: { label: 'Closed', tone: 'neutral' },
    controls: [],
  },
  // TWO entrances: #doCancel (discard selections, before an order exists) and #doCancelOrder's own
  // success path (an order was placed and a cancellation was recorded). Both call host.cancel() and
  // dispatch 'close' before landing here, so "control was returned" is true for both -- and the
  // detail must say NOTHING that is only true of one of them. It used to open with "Your changes
  // were discarded", which is false on the order path: nothing was discarded there, an order was
  // cancelled. The discard fact belongs to the selectionsDiscarded toast, which already carries it
  // on the one path where it is true.
  cancelled: {
    spinner: false,
    footer: false,
    terminal: true,
    icon: { glyph: '↩', tone: 'neutral' },
    title: 'Cancelled',
    detail: 'Control was returned to Encompass. If this window is still open, you can close it.',
    pill: { label: 'Cancelled', tone: 'neutral' },
    controls: [],
  },
  // The poll handler's tone === 'cancelled' branch, and #reconcileAfterCannotCancel: the order was
  // cancelled elsewhere -- in Encompass, or another tab -- not here, so nothing was discarded and
  // no close() was dispatched. 'cancelled' above would tell the officer control was handed back
  // when it was not; this gives them a real Close button instead of that false claim and controls: [].
  // Finding 5 of the 2026-10-14 UAT retest: a new order on a loan whose order is already
  // completed is refused at intake as a redraw, so the form is not offered at all.
  loanCompleted: {
    spinner: false,
    footer: true,
    terminal: true,
    icon: { glyph: '✓', tone: 'success' },
    title: 'This loan already has a completed order',
    detail: 'PBG has already prepared and delivered closing documents for this loan, so a new order '
      + "can't be placed. If something needs to change, contact PBG about a redraw.",
    pill: { label: 'Completed', tone: 'completed' },
    controls: ['viewCompleted', 'close'],
  },
  orderCancelled: {
    spinner: false,
    footer: false,
    terminal: true,
    icon: { glyph: '↩', tone: 'neutral' },
    title: 'Order cancelled',
    detail: 'This order was cancelled and PBG will not prepare documents for it. Close to return to Encompass.',
    pill: { label: 'Cancelled', tone: 'neutral' },
    controls: ['close'],
  },
};

/** Button copy. The footer's "Close & return" and the edge screens' "Return to Encompass" are the
 *  same action with the mock's two labels. */
export const LABELS = {
  retry: 'Try again',
  close: 'Return to Encompass',
  closeFooter: 'Close & return',
  cancel: 'Cancel',
  updateCredentials: 'Update credentials',
  updateCredentialsBusy: 'Opening secure flow…',
  startOrder: 'Start order',
  back: 'Back',
  placeOrder: 'Place order',
  viewStatus: 'View status',
  done: 'Done — close & return',
  redraw: 'Submit for redraw',
  refresh: 'Refresh status',
  refreshBusy: 'Checking…',
  retryOrder: 'Try again',
  support: 'Contact support',
  reconnect: 'Reconnect now',
  keepEditing: 'Keep editing',
  viewCompleted: 'View order',
  checkLoanAgain: 'Check again',
};

/** Officer-facing labels keyed by the wire values ICE carries on the transaction. Deliberately
 *  wider than REQUEST_TYPES in order-options.js: the product registers two types since 2026-09-29,
 *  but orders placed under the retired three still exist on the sandbox and must render with their
 *  names, not an em-dash. Add here whenever a type is added there; never remove. */
export const REQUEST_TYPE_LABELS = {
  CLOSING_DOC_DELIVERY: 'Closing documents',
  CLOSING_DOC_REVIEW: 'Document review',
  TITLE_REVIEW: 'Title review',
  CLOSING_DISCLOSURE: 'Closing Disclosure',
  ANCILLARY_DOCUMENT: 'Single document',
};

/** The three honest steps of screen 5. */
export const STEPS = [
  { title: 'Order received', desc: 'PBG has your request and loan context.' },
  { title: 'Generating documents', desc: 'Preparing your closing package.' },
  { title: 'Delivering to eFolder', desc: 'Filing documents to their eFolder targets.' },
];

/** The confirm dialogs (cert 1.13 for close/cancel, Phase 2 for redraw). None promises more than
 *  what happens. cancelOrder is live in both production and the stub as of F5 (canCancel true in
 *  both); cancelUnavailable is not dead weight even so -- #cancelDialog reads canCancel off
 *  whatever orders module the app is configured with, so it is what an operator sees if canCancel
 *  is ever rolled back to false without a deploy of this file. */
export const DIALOGS = {
  close: {
    icon: { glyph: '✓', tone: 'success' },
    title: 'Close and return to Encompass?',
    body: "We'll hand control back to Encompass. If you have placed an order it stays on file and "
      + 'continues delivering to the eFolder.',
    confirmLabel: 'Close & return',
    danger: false,
  },
  cancel: {
    icon: { glyph: '↩', tone: 'neutral' },
    title: 'Cancel and discard your selections?',
    body: 'This discards the selections you have made in this session and returns you to Encompass '
      + "without placing an order. This can't be undone.",
    confirmLabel: 'Discard & return',
    danger: true,
  },
  cancelOrder: {
    icon: { glyph: '↩', tone: 'neutral' },
    title: 'Cancel this order?',
    // Matches the orderCancelled toast's own standard: setting the cancel stamp stops anything
    // not yet started, but there is no lease on a delivery already running (see the BFF's Cancel
    // doc comment), so "nothing more will be filed" is a promise this screen cannot make.
    body: 'PBG will stop preparing your documents. Anything already underway may still be filed to '
      + "the eFolder. This returns you to Encompass and can't be undone.",
    confirmLabel: 'Cancel order & return',
    danger: true,
  },
  cancelUnavailable: {
    icon: { glyph: '↩', tone: 'neutral' },
    title: 'Return to Encompass?',
    body: 'Your order will continue; to cancel it, contact PBG. This returns you to Encompass '
      + 'without making any change to the order.',
    confirmLabel: 'Close & return',
    danger: false,
  },
  redraw: {
    icon: { glyph: '↻', tone: 'warn' },
    title: 'Submit this order for redraw?',
    body: "We'll re-prepare and re-deliver the documents on this completed order. "
      + 'The previous versions in the eFolder will be superseded. (Phase 2 capability.)',
    confirmLabel: 'Submit redraw',
    danger: false,
  },
};

export const TOASTS = {
  selectionsDiscarded: { type: 'info', title: 'Cancelled', desc: 'Your selections were discarded.' },
  // 202 means "we have this," not "it is done" (see the BFF's Cancel doc comment): work that
  // hasn't started yet will stop, but there is no lease on work already underway, so this states
  // what the 202 actually buys rather than a completion the endpoint does not promise.
  orderCancelled: { type: 'info', title: 'Order cancelled', desc: "We've recorded your cancellation. Anything not already underway will not start." },
  returned: { type: 'success', title: 'Returned to Encompass', desc: 'Control was handed back to the host.' },
  credentialsHandoff: { type: 'info', title: 'Handed to Encompass', desc: 'Update your PBG credentials in the secure flow that opens.' },
  delivered: { type: 'success', title: 'Documents delivered', desc: 'Your closing package is in the eFolder.' },
  preparationFailed: { type: 'danger', title: 'Preparation failed', desc: "We couldn't complete your order." },
  placeFailed: { type: 'danger', title: "We couldn't place your order", desc: 'Nothing was sent. Please try again in a moment.' },
  // A host TIMEOUT is not a refusal. Encompass may have created the transaction and simply not
  // answered in time, so "nothing was sent" would be a lie and "try again" would mint a second ICE
  // transaction on the loan. This says what we actually know and sends the officer to look first.
  placeTimedOut: {
    type: 'warn',
    title: "We didn't get a confirmation from Encompass",
    desc: "Encompass may still have created this order. Check this loan's services list before "
      + 'ordering again, or ordering again could place a second one.',
  },
  redrawSubmitted: { type: 'info', title: 'Redraw submitted', desc: "We're re-preparing your documents." },
  redrawFailed: { type: 'danger', title: "We couldn't submit the redraw", desc: 'Your delivered documents are unchanged. Please try again in a moment.' },
  cancelFailed: { type: 'danger', title: "We couldn't cancel the order", desc: 'The order is unchanged. Please try again or contact PBG.' },
  // CANNOT_CANCEL: the order is already terminal (completed, cancelled or failed), so this is not
  // cancelFailed's "unchanged, try again" -- there is nothing to retry. The server's own sentence
  // (ApiError.detail, from ProblemDetails' own "detail" field) replaces desc when the BFF sent one.
  cancelNotPossible: { type: 'info', title: "This order can't be cancelled", desc: "It's already reached a final status." },
  // ORIGIN_MISMATCH: a session problem, not a cancel problem -- the loan open in this Encompass
  // session is not the one this order belongs to, and retrying the cancel changes nothing.
  cancelSessionMismatch: { type: 'danger', title: "This session isn't for this order", desc: 'Reopen this loan from Encompass, then try again.' },
  stillProcessing: { type: 'info', title: 'Still processing', desc: "We'll show the result as soon as PBG reports it." },
  // A minute without an order number. Nothing is wrong -- the order is placed and Encompass has
  // it -- so this reassures and releases the officer rather than offering an action.
  delayed: { type: 'info', title: 'Still working on it', desc: 'You can return to Encompass; the order will keep going.' },
  statusUnavailable: { type: 'warn', title: 'Status unavailable', desc: "We couldn't read the order status just now. Refresh to try again." },
  // F2 picker outcomes. `documentsAttached.desc` is a function of the count; the app renders it.
  documentsAttached: { type: 'success', title: 'Documents attached', desc: (n) => (n === 1 ? '1 document will be sent with this order.' : `${n} documents will be sent with this order.`) },
  documentsUnavailable: { type: 'warn', title: "We couldn't open the eFolder", desc: 'Try again, or place the order without attachments.' },
  reconnected: { type: 'success', title: 'Reconnected', desc: 'Your secure session was restored.' },
  support: { type: 'info', title: 'Contact PBG support', desc: 'Quote the reference shown on this screen so we can find your order.' },
};

/** Which screen an API failure becomes. Anything unmapped is the generic error state, which is
 *  the safe default: it offers Retry and shows a correlation id. */
export function stateForError(error) {
  if (error?.errorCode === 'TENANT_NOT_ONBOARDED') return 'notOnboarded';
  // The partner access token lives 300 s; Encompass refusing it is a session to re-establish,
  // not a product that failed to load.
  if (error?.errorCode === 'PAT_REJECTED') return 'sessionExpired';
  return 'error';
}

/** The tone of a free-text order status from the BFF: best effort, label is always the status. */
export function toneForStatus(status) {
  const s = String(status ?? '').trim();
  if (/complete|deliver|done/i.test(s)) return 'completed';
  // BEFORE the failure test, and that order is the whole point: "canceled"/"cancelled" match
  // /cancel/ and used to fall into the failure branch, so an order the officer cancelled read as
  // "PBG could not prepare your documents". A cancellation is an outcome, not a fault.
  if (/cancel/i.test(s)) return 'cancelled';
  if (/fail|error|reject/i.test(s)) return 'failed';
  return 'processing';
}

/**
 * Which completed screen a BFF `delivery` value becomes.
 *
 * Unknown and absent both mean the plain completed screen. That is the pre-F3 behaviour, and an
 * older BFF that sends no `delivery` at all must keep reading exactly as it did -- falling through
 * to "delivering…" forever on a delivery that in fact happened would be worse than the small lie.
 */
export function stateForDelivery(delivery) {
  if (delivery === 'pending') return 'completedDeliveryPending';
  if (delivery === 'failed') return 'completedDeliveryFailed';
  return 'completed';
}

export function pillForOrder(order) {
  const status = (order?.status ?? '').trim();
  if (!status) return { label: 'Existing order', tone: 'processing' };
  return { label: displayStatus(status), tone: toneForStatus(status) };
}

/** "processing" -> "Processing". The BFF capitalises its statuses; the stub's lifecycle does not. */
export function displayStatus(status) {
  const s = String(status ?? '').trim();
  return s ? s.charAt(0).toUpperCase() + s.slice(1) : s;
}

function firstNames(borrowers) {
  const names = (borrowers ?? []).map((b) => String(b).trim().split(/\s+/)[0]).filter(Boolean);
  if (!names.length) return 'this loan';
  if (names.length === 1) return names[0];
  return `${names.slice(0, -1).join(', ')} & ${names[names.length - 1]}`;
}

/**
 * The documents panel's four states (F2 spec, "UI states and copy"). Copy is the spec's, verbatim:
 * these are reviewed officer-facing sentences rendered inside a third-party lender's Encompass.
 *
 * `summary` is a function because three of the four quote live counts.
 */
export const DOCUMENT_STATES = {
  pulling: {
    summary: (d) => `Receiving documents… (${d.received} of ${d.expected})`,
  },
  received: {
    summary: (d) => `Documents received (${d.received})`,
  },
  partial: {
    summary: (d) => `${d.received} received, ${d.failed} couldn’t be retrieved`,
    // The officer can fix this themselves, but only if we say which files and that re-attaching
    // is the fix. Without this line "couldn't be retrieved" is a dead end.
    hint: 'We’ll keep trying; you can also re-attach them in Encompass.',
  },
  none: {
    summary: () => 'No documents were attached. You can attach documents in Encompass at any time.',
  },
};
